import { useContext, useMemo } from 'react';
import { WidgetPlugin, WidgetSlot } from '../../../types/widgetPlugins';
import { WidgetDataContext } from '../widgetDataContext';
import { useTranslation } from '../../useTranslation';
import { PluginsContext } from '../pluginsContext';
import { usePluginsUpdateMode } from './usePluginsUpdateMode';
import { useInstalledPlugins } from './useInstalledPlugins';
import { usePluginsBi } from './useBi';
import {
  mapSlotsForReplaceModal,
  mapWidgetPluginsForReplaceModal,
} from '../utils/utils';
import { PluginActionContext } from '@wix/editor-platform-host-integration-apis';
import { serializePlacement } from '../../../apis/services/plugins/utils';
import { ReplacementOption } from '@wix/editor-platform-shared-ui';

export type WidgetSlotOrPlugin = WidgetSlot | WidgetPlugin | undefined;

export function useWidgetPluginManager() {
  const {
    editorIntegrationAPI,
    widgetRef,
    fedopsLogger,
    widgetName,
    widgetSlots,
  } = useContext(WidgetDataContext);
  const { resetPluginInstallationStateBySlot, setPanelError } =
    useContext(PluginsContext);
  const { startUpdateMode, stopUpdateMode } = usePluginsUpdateMode();
  const installedPlugins = useInstalledPlugins();
  const bi = usePluginsBi();
  const [t] = useTranslation();

  const PANELS = {
    widgetPluginsManager: 'panels.widgetPanels.pluginsPanel',
    pluginReplace: 'tpaPanels.confirmation.pluginReplace',
    pluginDeleteOrReplace: 'tpaPanels.confirmation.pluginDeleteOrReplace',
  };

  return useMemo(() => {
    function getSlot(slotOrPlugin: WidgetSlotOrPlugin): WidgetSlot | undefined {
      if ((slotOrPlugin as WidgetSlot)?.compRef) {
        return slotOrPlugin as WidgetSlot;
      }
      return (slotOrPlugin as WidgetPlugin)?.slot;
    }

    function isPluginInstalled(widgetPlugin: WidgetPlugin) {
      return (
        widgetPlugin.slot?.pluginInfo?.widgetId === widgetPlugin.widgetRefId
      );
    }

    function isSlotPopulatedInstalled(slotOrPlugin: WidgetSlotOrPlugin) {
      return !!getSlot(slotOrPlugin)?.pluginInfo;
    }

    async function installOrReplacePlugin(widgetPlugin: WidgetPlugin) {
      const slot = widgetPlugin.slot;

      if (!slot) {
        return;
      }

      try {
        startUpdateMode();

        if (!slot.pluginInfo) {
          const pluginActionContext: PluginActionContext = {
            biOrigin: 'plugin_market_card',
            controlMode: 'user',
          };
          fedopsLogger.interactionStarted('plugin-installation-interaction');

          await installPlugin(widgetPlugin, slot, pluginActionContext);

          fedopsLogger.interactionEnded('plugin-installation-interaction');

          return;
        }

        const [replacementSlot] = await confirmPluginReplace(widgetPlugin);

        if (replacementSlot) {
          const pluginActionContext: PluginActionContext = {
            biOrigin: 'plugin_market_card_replace',
            controlMode: 'user',
          };
          fedopsLogger.interactionStarted('plugin-replacement-interaction');

          await _uninstallPlugin(replacementSlot, pluginActionContext);
          await installPlugin(
            widgetPlugin,
            replacementSlot,
            pluginActionContext,
          );

          fedopsLogger.interactionEnded('plugin-replacement-interaction');
        }
      } finally {
        stopUpdateMode();
      }
    }

    async function installPlugin(
      widgetPlugin: WidgetPlugin,
      slot = widgetPlugin.slot,
      context: PluginActionContext,
    ) {
      if (!slot) {
        return;
      }

      try {
        await editorIntegrationAPI.installWidgetPlugin(
          slot.compRef,
          {
            appDefinitionId: widgetPlugin.appDefinitionId,
            widgetId: widgetPlugin.widgetRefId,
            version: widgetPlugin.version,
          },
          context,
        );
      } catch (e: any) {
        setPanelError(e);
        throw new Error(e.message);
      } finally {
        editorIntegrationAPI.closePanel(PANELS.widgetPluginsManager);
        editorIntegrationAPI.unhighlightSlot(slot.compRef);
      }
    }

    async function uninstallPlugin(
      slot: WidgetSlot,
      context: PluginActionContext,
    ) {
      try {
        startUpdateMode();

        const biPayload = {
          slot_id: slot.compRef.id,
          origin: context.biOrigin,
          plugin_id: slot.pluginInfo?.widgetId!,
          host_app_id: slot.placement?.appDefinitionId || '',
          placement_id: serializePlacement(slot.placement),
          app_def_id_plugin: slot.pluginInfo?.appDefinitionId,
        };

        bi.pluginRemoveClick(biPayload);

        if (await confirmPluginUninstall(slot)) {
          fedopsLogger.interactionStarted('plugin-uninstallation-interaction');

          await _uninstallPlugin(slot, context);

          resetPluginInstallationStateBySlot(slot);
          editorIntegrationAPI.unhighlightSlot(slot.compRef);
          fedopsLogger.interactionEnded('plugin-uninstallation-interaction');

          // uninstalling last plugin
          if (installedPlugins.length === 1) {
            editorIntegrationAPI.closePanel(PANELS.widgetPluginsManager);
          }
        }
      } finally {
        stopUpdateMode();
      }
    }

    async function _uninstallPlugin(
      slot: WidgetSlot,
      pluginActionContext?: PluginActionContext,
    ) {
      try {
        await editorIntegrationAPI.uninstallWidgetPlugin(
          slot.compRef,
          pluginActionContext,
        );
      } catch (e: any) {
        setPanelError(e);
        throw new Error(e.message);
      }
    }

    async function confirmPluginUninstall(slotOrPlugin: WidgetSlotOrPlugin) {
      const slot = getSlot(slotOrPlugin);
      if (!slot) {
        return;
      }
      return new Promise((resolve) => {
        editorIntegrationAPI.openPanel(
          PANELS.pluginDeleteOrReplace,
          {
            replace: false,
            pluginName:
              slot.pluginInfo?.name ??
              t('PLATFORM_Widget_Slots_default_plugin_name'),
            onConfirm: () => resolve(true),
            onCancel: () => resolve(false),
          },
          true,
        );
      });
    }

    async function confirmPluginReplace(
      widgetPlugin: WidgetPlugin,
    ): Promise<WidgetSlot[]> {
      if (!widgetSlots.length || !widgetPlugin.slot?.pluginInfo) {
        return [];
      }

      const closePanel = () => {
        editorIntegrationAPI.closePanel(PANELS.pluginReplace);
      };

      return new Promise((resolve) => {
        const plugins = mapWidgetPluginsForReplaceModal([widgetPlugin]);
        const replaceModalSlots = mapSlotsForReplaceModal(
          widgetSlots,
          widgetName,
        );

        const onCancel = () => {
          resolve([]);
          closePanel();
        };

        const onReplace = (replacementOptions: ReplacementOption[]) => {
          const [replacementOption] = replacementOptions;

          const replacementSlot = widgetSlots.find(
            (slot) =>
              slot.placement.slotId === replacementOption.placement.slotId &&
              slot.placement.widgetId === replacementOption.placement.widgetId,
          )!;

          resolve([replacementSlot]);
          closePanel();
        };

        const onHelp = () => {
          editorIntegrationAPI.openReplacePluginHelpArticle?.();
        };

        editorIntegrationAPI.openPanel(
          PANELS.pluginReplace,
          {
            width: '720px',
            plugins,
            slots: replaceModalSlots,
            onCancel,
            onReplace,
            onHelp,
            onClose: onCancel,
            cancelActionType: 'cancel',
            origin: 'editor',
            locale: window.editorModel?.languageCode,
          },
          true,
        );
      });
    }

    async function highlightSlot(slot?: WidgetSlot, unhighlightInMs = 0) {
      if (!slot) {
        return;
      }

      if (editorIntegrationAPI.isBlocks()) {
        return;
      }

      await editorIntegrationAPI.highlightSlot(slot.compRef);

      if (unhighlightInMs) {
        setTimeout(() => unhighlightSlot(slot), unhighlightInMs);
      }
    }

    async function unhighlightSlot(slot?: WidgetSlot) {
      if (!slot) {
        return;
      }

      if (editorIntegrationAPI.isBlocks()) {
        return;
      }

      editorIntegrationAPI.unhighlightSlot(slot.compRef);
    }

    async function showOnPage(slot?: WidgetSlot, unhighlightInMs = 0) {
      if (!slot) {
        return;
      }

      if (editorIntegrationAPI.isBlocks()) {
        return;
      }

      await editorIntegrationAPI.showOnPage(slot.compRef);

      if (unhighlightInMs) {
        setTimeout(() => unhighlightSlot(slot), unhighlightInMs);
      }
    }

    function isPluginMoveActionEnabled(slot: WidgetSlot | undefined) {
      if (!slot) {
        return false;
      }

      try {
        return editorIntegrationAPI.isWidgetPluginMoveActionEnabled(
          slot.compRef,
        );
      } catch (e) {
        return false;
      }
    }

    function movePlugin(slot: WidgetSlot) {
      editorIntegrationAPI.movePlugin(slot.compRef);
    }

    return {
      widgetRef,
      isSlotPopulatedInstalled,
      isPluginInstalled,
      isPluginMoveActionEnabled,
      installOrReplacePlugin,
      uninstallPlugin,
      highlightSlot,
      unhighlightSlot,
      showOnPage,
      movePlugin,
    };
  }, [
    widgetRef,
    startUpdateMode,
    fedopsLogger,
    stopUpdateMode,
    bi,
    editorIntegrationAPI,
    resetPluginInstallationStateBySlot,
    installedPlugins,
    widgetSlots,
    t,
  ]);
}
